/**
 * Transition screen (transition tool, AI).
 *
 * Flow: 1 Pick a start frame and an end frame → 2 Choose a motion → 3 Length and
 * quality → Generate. The AI makes the video that travels from the first photo to
 * the second. The motions are named presets; the wording is server-owned, so the
 * app only sends `options.transitionMotion` (never text). The price is the video
 * matrix from GET /credits/pricing, defaulting to the cheapest option (360p, 5s).
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { toast, toastApiError } from '../ui/toast.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { requireAccount, requireSignIn } from '../state/gate.js';
import { withUploadGuide } from '../components/uploadGuide.js';
import type { CreditPricingConfig, JobOptions, QualityTier, TransitionMotion, UploadFileMeta, VideoDurationSec } from '@loomia/shared';

const TOOL = 'transition' as const;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const CLIENT_MAX_BYTES = 25 * 1024 * 1024;
const TIER_ORDER: QualityTier[] = ['360p', '540p', '720p', '1080p'];

const MOTIONS: ReadonlyArray<{ id: TransitionMotion; label: string; icon: string }> = [
  { id: 'smooth_morph', label: 'Smooth', icon: 'auto_awesome' },
  { id: 'camera_push', label: 'Push in', icon: 'movie' },
  { id: 'turn_to_camera', label: 'Turn', icon: 'person' },
  { id: 'dreamy', label: 'Dreamy', icon: 'blur_on' },
  { id: 'zoom_through', label: 'Zoom', icon: 'zoom_in' },
];

type Which = 'start' | 'end';

interface Frame {
  status: 'empty' | 'uploading' | 'ready' | 'error';
  progress: number;
  previewUrl: string | null;
  fileId: string | null;
  error: string | null;
}

const emptyFrame = (): Frame => ({ status: 'empty', progress: 0, previewUrl: null, fileId: null, error: null });

let renderSeq = 0;
let prevCleanup: (() => void) | null = null;

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export function renderTransition(root: HTMLElement, query: URLSearchParams): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  const load = async (): Promise<void> => {
    if (prevCleanup) {
      prevCleanup();
      prevCleanup = null;
    }
    root.innerHTML = '<div class="co"><div class="co-skel"></div></div>';
    const [pricingR] = await Promise.allSettled([api.getPricing(), refreshCredits().catch(() => null)]);
    if (!alive()) return;
    const matrix = pricingR.status === 'fulfilled' ? pricingR.value.perTool[TOOL]?.perTierDuration : undefined;
    if (pricingR.status === 'rejected' || !matrix || Object.keys(matrix).length === 0) {
      root.innerHTML = `
        <div class="co"><div class="co-state">${icon('cloud_off')}
          <p>Couldn't load Transition. Check your connection.</p>
          <button class="btn btn-secondary" data-act="retry">Try again</button></div></div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }
    mount(pricingR.value);
  };

  const mount = (pricing: CreditPricingConfig): void => {
    const matrix = pricing.perTool[TOOL]?.perTierDuration ?? {};
    const tiers = TIER_ORDER.filter((t) => matrix[t]);
    const durations: VideoDurationSec[] = pricing.perTool[TOOL]?.durations?.length ? pricing.perTool[TOOL]!.durations! : [5, 10, 15];

    let frames: Record<Which, Frame> = { start: emptyFrame(), end: emptyFrame() };
    let motion: TransitionMotion = 'smooth_morph';
    let generating = false;
    let pickTarget: Which = 'start';
    let duration: VideoDurationSec = (durations as number[]).includes(Number(query.get('duration')))
      ? (Number(query.get('duration')) as VideoDurationSec)
      : durations[0]!;
    const wantedTier = query.get('quality');
    let tier: QualityTier = (tiers as string[]).includes(wantedTier ?? '') ? (wantedTier as QualityTier) : tiers[0]!;

    const cost = (): number => matrix[tier]?.[duration] ?? 0;
    const missing = (): string | null => {
      if (frames.start.status === 'uploading' || frames.end.status === 'uploading') return 'Uploading…';
      if (frames.start.status !== 'ready') return 'Add a start frame';
      if (frames.end.status !== 'ready') return 'Add an end frame';
      return null;
    };

    // -- Markup -----------------------------------------------------------

    const step = (n: number, title: string): string => `<div class="co-step"><span class="co-num">${n}</span><h2>${title}</h2></div>`;

    const frameHtml = (which: Which): string => {
      const f = frames[which];
      const name = which === 'start' ? 'Start Frame' : 'End Frame';
      const inner =
        f.status === 'empty'
          ? `<span class="tf-empty">${icon('add_photo_alternate')}<span>Add photo</span></span>`
          : `${f.previewUrl ? `<img src="${escapeHtml(f.previewUrl)}" alt="${name}" />` : ''}
             ${f.status === 'uploading' ? `<span class="co-bar"><span style="width:${Math.round(f.progress * 100)}%"></span></span>` : ''}
             ${f.status === 'error' ? `<span class="tf-tag">${escapeHtml(f.error ?? 'Upload failed')}</span>` : '<span class="tf-tag">Change</span>'}`;
      return `
        <div class="tf-col">
          <span class="tf-label">${name}</span>
          <button class="tf-frame is-${f.status}" data-pick="${which}" aria-label="${f.status === 'empty' ? `Add ${name.toLowerCase()}` : `Change ${name.toLowerCase()}`}">${inner}</button>
        </div>`;
    };

    const motionsHtml = (): string => `
      <div class="co-types co-types-5" role="radiogroup" aria-label="Motion">
        ${MOTIONS.map(
          (m) => `
          <button class="co-type${m.id === motion ? ' selected' : ''}" role="radio" aria-checked="${m.id === motion}" data-motion="${m.id}">
            <span class="co-type-ico">${icon(m.icon)}</span><span>${escapeHtml(m.label)}</span>
          </button>`,
        ).join('')}
      </div>`;

    const optionsHtml = (): string => `
      <p class="co-sub">Length</p>
      <div class="co-seg" role="radiogroup" aria-label="Video length">
        ${durations
          .map((d) => `<button class="co-seg-opt${d === duration ? ' selected' : ''}" role="radio" aria-checked="${d === duration}" data-duration="${d}">${d}s</button>`)
          .join('')}
      </div>
      <p class="co-sub">Quality</p>
      <div class="co-seg" role="radiogroup" aria-label="Quality">
        ${tiers
          .map((t) => `<button class="co-seg-opt${t === tier ? ' selected' : ''}" role="radio" aria-checked="${t === tier}" data-tier="${t}">${t}</button>`)
          .join('')}
      </div>
      <p class="co-sub">Higher quality and longer videos cost more credits.</p>`;

    const footerHtml = (): string => {
      const c = cost();
      const bal = getBalance();
      const short = bal !== null && bal < c ? c - bal : 0;
      const why = missing();
      const btn =
        short > 0 && !why
          ? '<button class="btn btn-primary btn-block" data-act="buy">Get more credits</button>'
          : `<button class="btn btn-primary btn-block" data-act="generate" ${generating ? 'disabled' : ''}>${
              generating ? 'Starting…' : why ? escapeHtml(why) : `Generate Video ${icon('toll', { fill: true })} ${c}`
            }</button>`;
      return `
        <p class="co-sub co-refund">Failed runs are refunded automatically.</p>
        ${short > 0 ? `<p class="co-warn">You need ${icon('toll', { fill: true })} ${short} more credits</p>` : ''}
        ${btn}`;
    };

    const paint = (): void => {
      root.innerHTML = `
        <div class="co">
          <header class="co-head">
            <button class="co-back" data-act="back" aria-label="Back">${icon('arrow_back')}</button>
            <div class="co-head-text"><h1>Transition</h1><p>Turn two photos into one AI video.</p></div>
            <a class="co-credits" href="#/paywall" aria-label="Get credits">${icon('toll', { fill: true })} ${getBalance() ?? '–'} <span>+</span></a>
          </header>
          ${step(1, 'Choose your frames')}
          <div class="tf-frames">
            ${frameHtml('start')}
            <button class="tf-swap" data-act="swap" aria-label="Swap start and end frame" ${frames.start.status === 'empty' && frames.end.status === 'empty' ? 'disabled' : ''}>${icon('swap_horiz')}</button>
            ${frameHtml('end')}
          </div>
          <p class="co-sub">Photos of the same person or scene work best. The video starts on the first photo and ends on the second.</p>
          <p class="co-note tf-note">${icon('info')}Only use photos of yourself or people who agreed.</p>
          ${step(2, 'Choose a motion')}
          ${motionsHtml()}
          ${step(3, 'Length and quality')}
          ${optionsHtml()}
          <footer class="co-foot" id="co-foot">${footerHtml()}</footer>
          <input type="file" id="co-file" accept="${ACCEPTED_TYPES.join(',')}" hidden />
        </div>`;
    };

    const paintFooter = (): void => {
      const f = root.querySelector<HTMLElement>('#co-foot');
      if (f) f.innerHTML = footerHtml();
    };

    // -- Upload -----------------------------------------------------------

    const upload = async (which: Which, file: File): Promise<void> => {
      if (!requireSignIn('Sign in to add your photo.')) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast('Please choose a JPEG, PNG or WebP image.');
        return;
      }
      if (file.size > CLIENT_MAX_BYTES) {
        toast('That image is too large. Please pick one under 25 MB.');
        return;
      }
      const old = frames[which];
      if (old.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(old.previewUrl);
      if (old.fileId) void api.deleteUpload(old.fileId).catch(() => undefined);
      const mine: Frame = { status: 'uploading', progress: 0, previewUrl: URL.createObjectURL(file), fileId: null, error: null };
      frames[which] = mine;
      paint();
      try {
        const req = await api.requestUpload({ purpose: 'generation_input', contentType: file.type, contentLength: file.size });
        await api.putUploadBytes(req.uploadUrl, file, req.uploadHeaders, (r) => {
          if (!alive() || frames[which] !== mine) return;
          mine.progress = r;
          const bar = root.querySelector<HTMLElement>(`[data-pick="${which}"] .co-bar span`);
          if (bar) bar.style.width = `${Math.round(r * 100)}%`;
        });
        const meta: UploadFileMeta = await api.completeUpload(req.fileId);
        if (!alive() || frames[which] !== mine) {
          if (meta.id) void api.deleteUpload(meta.id).catch(() => undefined);
          return;
        }
        if (meta.status !== 'ready') throw new Error(meta.error || 'Image processing failed.');
        mine.status = 'ready';
        mine.fileId = meta.id;
        mine.previewUrl = meta.thumbnails?.thumb256 ?? meta.url ?? mine.previewUrl;
      } catch (err) {
        if (!alive() || frames[which] !== mine) return;
        mine.status = 'error';
        mine.error = err instanceof Error ? err.message : 'Upload failed.';
        toastApiError(err, 'Upload failed. Please try again.');
      }
      paint();
    };

    // -- Generate ---------------------------------------------------------

    const generate = async (): Promise<void> => {
      if (generating) return;
      if (!requireAccount('Sign in to generate.')) return;
      const why = missing();
      if (why) {
        toast(why);
        // No photo yet: open the picker right away instead of only saying so.
        if (/^Add /.test(why)) root.querySelector<HTMLElement>('[data-act="pick"]')?.click();
        return;
      }
      const c = cost();
      let bal = getBalance();
      if (bal === null) bal = await refreshCredits().catch(() => null);
      if (!alive()) return;
      if (bal !== null && bal < c) {
        navigate('/paywall');
        return;
      }
      generating = true;
      paintFooter();
      try {
        const options: JobOptions = { transitionMotion: motion };
        const { job } = await api.submitJob({
          tool: TOOL,
          inputFileIds: [frames.start.fileId as string, frames.end.fileId as string],
          options,
          qualityTier: tier,
          durationSec: duration,
          idempotencyKey: newId(),
        });
        void refreshCredits().catch(() => undefined);
        navigate(`/result/${job.id}`);
      } catch (err) {
        if (!alive()) return;
        if (err instanceof ApiError && (err.status === 402 || /insufficient|credit/i.test(err.code))) {
          toast('Not enough credits.');
          navigate('/paywall');
        } else {
          toastApiError(err, 'Could not start generation. Please try again.');
        }
      } finally {
        if (alive()) {
          generating = false;
          paintFooter();
        }
      }
    };

    // -- Events (delegated) -----------------------------------------------

    root.onclick = (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-pick],[data-motion],[data-duration],[data-tier]');
      if (!el || (el as HTMLButtonElement).disabled) return;
      const d = el.dataset;
      if (d.pick) {
        if (requireSignIn('Sign in to add your photo.')) {
          pickTarget = d.pick === 'end' ? 'end' : 'start';
          withUploadGuide('transition', () => root.querySelector<HTMLInputElement>('#co-file')?.click());
        }
      } else if (d.act === 'swap') {
        frames = { start: frames.end, end: frames.start };
        paint();
      } else if (d.act === 'back') {
        if (history.length > 1) history.back();
        else navigate('/');
      } else if (d.act === 'generate') void generate();
      else if (d.act === 'buy') navigate('/paywall');
      else if (d.motion) {
        motion = MOTIONS.find((x) => x.id === d.motion)?.id ?? motion;
        paint();
      } else if (d.duration) {
        duration = durations.find((x) => x === Number(d.duration)) ?? duration;
        paint();
      } else if (d.tier) {
        tier = tiers.find((x) => x === d.tier) ?? tier;
        paint();
      }
    };

    root.onchange = (e) => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'co-file') return;
      const f = input.files?.[0];
      input.value = '';
      if (f) void upload(pickTarget, f);
    };

    const unsub = creditsChanged.on(() => {
      if (alive()) paintFooter();
    });
    prevCleanup = () => {
      unsub();
      for (const f of Object.values(frames)) if (f.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(f.previewUrl);
    };

    paint();
  };

  void load();
}
